import { Alert, Button, Input, Modal, Radio, Select } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import RequiredMark from "@/views/components/RequiredMark";
import DetailPanel from "./components/detailPanel";
import { useUserStore } from "@/views/stores";
import {
    ANNUAL_MEMBER_STATE,
    FREE_NUM_UNIT,
    GIFT_DURATION_UNIT,
    GIFT_MEMBER_STATE,
    VISIBLE_CONTENT_STATE
} from "@zhihaoscm/meta";
import "./MemberLevelPage.scss";

export interface MemberLevelRecord {
    id: number;
    name?: string;
    state?: number | string;
    yearFee?: number;
    yearDiscountFee?: number;
    yearDiscountFlag?: number;
    giftFlag?: number;
    giftDuration?: number;
    giftDurationUnit?: number;
    memberPermission?: Record<string, unknown>;
    feature?: Record<string, unknown>;
}

export interface MemberLevelPageProps {
    loadLevels?: () => Promise<MemberLevelRecord[]>;
    updateState?: (id: number, state: number) => Promise<unknown>;
    loadRegisterConfig?: (type: number) => Promise<RegisterGiveConfig>;
    saveRegisterConfig?: (id: number, params: RegisterGiveConfigPayload) => Promise<unknown>;
}

interface RegisterGiveContent {
    hasOpen?: number;
    level?: number;
    duration?: number;
    effectiveDuration?: number;
}

interface RegisterGiveConfig {
    id?: number;
    content?: string;
}

interface RegisterGiveConfigPayload {
    type: number;
    content: string;
}

export interface MemberLevelFormPageProps {
    memberId?: number;
    loadDetail?: (id: number) => Promise<MemberLevelRecord>;
    updateMember?: (record: MemberLevelRecord) => Promise<unknown>;
}

/** 会员等级状态沿用 Vue MEMBER_OPEN_STATE：1 开放，0 关闭。 */
const isOpen = (record: MemberLevelRecord) => Number(record.state) === 1;

/** 会员费用沿用 Vue yearDiscountFee || yearFee 的展示优先级。 */
const formatFee = (record: MemberLevelRecord) => {
    const fee = record.yearDiscountFee || record.yearFee;
    return fee == null ? DEFAULT_VALUE : `¥${Number(fee).toFixed(2)}/年`;
};

/** 会员卡片背景沿用旧 Vue 按会员等级编号划分的三种视觉样式。 */
const getMemberCardVariant = (id: number) => {
    if (id === 1) return "free";
    if (id === 2) return "base";
    if (id === 3) return "senior";
    return "default";
};

/** 复用 Vue `/admin/membership-level/all` 等级列表接口。 */
export async function loadMemberLevels(): Promise<MemberLevelRecord[]> {
    const response = await request.service.get("/admin/membership-level/all");
    return Array.isArray(response.data) ? response.data : [];
}

/** 复用 Vue `/admin/membership-level/:id/state/:state` 状态接口。 */
export async function updateMemberLevelState(id: number, state: number): Promise<unknown> {
    const response = await request.service.put(`/admin/membership-level/${id}/state/${state}`);
    return response.data;
}

/** 复用旧 Vue 注册送会员配置读取接口，业务设置类型固定为 2。 */
export async function loadRegisterGiveConfig(type: number): Promise<RegisterGiveConfig> {
    const response = await request.service.get(`/admin/business-config/type?type=${type}`);
    return response.data;
}

/** 复用旧 Vue 注册送会员配置保存接口，保持 type/content JSON 契约。 */
export async function saveRegisterGiveConfig(id: number, params: RegisterGiveConfigPayload): Promise<unknown> {
    const response = await request.service.put(`/admin/business-config/${id}`, params);
    return response.data;
}

const parseRegisterGiveContent = (content?: string): RegisterGiveContent => {
    if (!content) return {};
    try {
        return JSON.parse(content) as RegisterGiveContent;
    } catch {
        return {};
    }
};

/** staging 当前详情接口为 `/admin/membership-level/:id`，与旧版 service 的废弃 `/vo/:id` 入口保持返回结构兼容。 */
export async function loadMemberLevelDetail(id: number): Promise<MemberLevelRecord> {
    const response = await request.service.get(`/admin/membership-level/${id}`);
    return response.data;
}

/** 复用 Vue `/admin/membership-level` 会员等级更新接口。 */
export async function updateMemberLevel(record: MemberLevelRecord): Promise<unknown> {
    const response = await request.service.put("/admin/membership-level", record);
    return response.data;
}

interface MemberPermissionOption {
    key: string;
    label: string;
    layout: number;
    props: Record<string, unknown>;
}

/** 创建旧 Vue 会员权限配置的默认模型，避免把配置退化成不可编辑的 JSON 文本。 */
function createPermissionOptions(): MemberPermissionOption[] {
    return [
        {
            key: "productTypeIndexCheck",
            label: "砂石指数价格查看",
            layout: 1,
            props: { hasUnLimit: undefined, visibleRange: [1] }
        },
        {
            key: "shippingPriceIndexCheck",
            label: "运价指数价格查看",
            layout: 1,
            props: { hasUnLimit: undefined, visibleRange: [1] }
        },
        { key: "shipQuery", label: "查船舶", layout: 2, props: { hasUnLimit: undefined, times: undefined } },
        { key: "carQuery", label: "查车辆", layout: 2, props: { hasUnLimit: undefined, times: undefined } },
        { key: "memberInformation", label: "会员专享资讯", layout: 3, props: { hasUnLimit: undefined } },
        {
            key: "intelligentAssistant",
            label: "智能助手功能",
            layout: 2,
            props: { hasUnLimit: undefined, times: undefined }
        },
        { key: "manualService", label: "专属客服1V1", layout: 4, props: { hasSupport: undefined } },
        {
            key: "subAccountNum",
            label: "企业子账号",
            layout: 5,
            props: { hasSupport: undefined, supportNum: undefined }
        },
        { key: "offlineBooth", label: "线下活动展位", layout: 6, props: { hasSupport: undefined } },
        { key: "mediaPromotion", label: "自媒体推广", layout: 7, props: { hasSupport: undefined, freeNumUnit: 2 } }
    ];
}

/** 将旧接口 feature/memberPermission 回显为可编辑的权限行。 */
function hydratePermissionOptions(permission: Record<string, unknown> | undefined): MemberPermissionOption[] {
    const source = permission || {};
    return createPermissionOptions().map(option => {
        const value = source[option.key];
        if (option.key === "memberInformation") return { ...option, props: { hasUnLimit: value ? 0 : 1 } };
        if (option.key === "manualService") return { ...option, props: { hasSupport: Number(value) || 0 } };
        if (option.key === "subAccountNum") {
            const supportNum = Number(value) || 0;
            return { ...option, props: { hasSupport: supportNum > 0 ? 1 : 0, supportNum: supportNum || undefined } };
        }
        if (value && typeof value === "object")
            return { ...option, props: { ...option.props, ...(value as Record<string, unknown>) } };
        return option;
    });
}

/** 按旧 Vue save() 规则组装会员权限接口对象。 */
function buildMemberPermission(options: MemberPermissionOption[]): Record<string, unknown> {
    const byKey = Object.fromEntries(options.map(option => [option.key, option.props]));
    const value = (key: string) => byKey[key] || {};
    const limited = (key: string, field: string) => {
        const props = value(key);
        return { hasUnLimit: props.hasUnLimit, [field]: props.hasUnLimit ? undefined : props[field] };
    };
    const support = (key: string, ...fields: string[]) => {
        const props = value(key);
        return {
            hasSupport: props.hasSupport,
            ...Object.fromEntries(fields.map(field => [field, props.hasSupport ? props[field] : undefined]))
        };
    };
    const member = value("memberInformation");
    const service = value("manualService");
    const subAccount = value("subAccountNum");
    return {
        productTypeIndexCheck: limited("productTypeIndexCheck", "visibleRange"),
        shippingPriceIndexCheck: limited("shippingPriceIndexCheck", "visibleRange"),
        shipQuery: limited("shipQuery", "times"),
        carQuery: limited("carQuery", "times"),
        memberInformation: member.hasUnLimit ? 0 : 1,
        intelligentAssistant: limited("intelligentAssistant", "times"),
        manualService: service.hasSupport,
        subAccountNum: subAccount.hasSupport ? subAccount.supportNum : 0,
        offlineBooth: support("offlineBooth", "boothNum", "joinNum", "quotaAmount"),
        mediaPromotion: {
            ...support("mediaPromotion", "freeNum"),
            freeNumUnit: value("mediaPromotion").freeNumUnit || 2
        }
    };
}

const numberValue = (value: unknown) => (value === "" || value == null ? undefined : Number(value));

/** 会员等级编辑页，保留旧 Vue 的费用、赠送和十类权限配置交互。 */

export function MemberLevelPage({
    loadLevels = loadMemberLevels,
    updateState = updateMemberLevelState,
    loadRegisterConfig = loadRegisterGiveConfig,
    saveRegisterConfig = saveRegisterGiveConfig
}: MemberLevelPageProps) {
    const navigate = useNavigate();
    const banButton = useUserStore(state => state.banButton);
    const [levels, setLevels] = useState<MemberLevelRecord[]>([]);
    const [errorMessage, setErrorMessage] = useState("");
    const [confirmState, setConfirmState] = useState<{ id: number; nextState: number; name: string }>();
    const [registerConfig, setRegisterConfig] = useState<RegisterGiveContent>({});
    const [savedRegisterConfig, setSavedRegisterConfig] = useState<RegisterGiveContent>({});
    const [registerConfigId, setRegisterConfigId] = useState(2);
    const [registerEditing, setRegisterEditing] = useState(false);

    const reload = async () => {
        try {
            setLevels(await loadLevels());
            setErrorMessage("");
        } catch {
            setLevels([]);
            setErrorMessage("会员等级加载失败");
        }
    };

    useEffect(() => {
        void reload();
        void loadRegisterConfig(2)
            .then(config => {
                const content = parseRegisterGiveContent(config.content);
                setRegisterConfigId(config.id || 2);
                setRegisterConfig(content);
                setSavedRegisterConfig(content);
            })
            .catch(() => undefined);
        // 首次加载只依赖外部 Vue API 适配器，避免每次状态更新重复请求。
    }, [loadLevels, loadRegisterConfig]);

    const openRegisterLevels = levels.filter(record => Number(record.state) === 1 && record.id > 1);
    const updateRegisterField = <K extends keyof RegisterGiveContent>(key: K, value: RegisterGiveContent[K]) =>
        setRegisterConfig(current => ({ ...current, [key]: value }));
    const saveRegister = async () => {
        const content = JSON.stringify(registerConfig);
        await saveRegisterConfig(registerConfigId, { type: 2, content });
        setSavedRegisterConfig(registerConfig);
        setRegisterEditing(false);
    };

    const openGrade = useMemo(
        () => levels.reduce((highest, record) => (isOpen(record) && record.id > highest ? record.id : highest), 1),
        [levels]
    );

    const handleState = (record: MemberLevelRecord) => {
        const nextState = isOpen(record) ? 0 : 1;
        if (record.id < openGrade) {
            setErrorMessage("无法关闭，请检查会员等级列表");
            return;
        }
        if (record.id > openGrade + 1) {
            setErrorMessage("无法开放，请检查会员等级列表");
            return;
        }
        setConfirmState({ id: record.id, nextState, name: record.name || "会员等级" });
    };

    const confirmStateChange = async () => {
        if (!confirmState) return;
        await updateState(confirmState.id, confirmState.nextState);
        setConfirmState(undefined);
        await reload();
    };

    return (
        <section aria-label="member-level-page" className="member-level-page">
            <h1 className="member-level-page__title">会员等级配置</h1>
            <div className="member-level-section">
                <div className="member-level-section__title">
                    <span className="member-level-section__title-tag" aria-hidden="true" />
                    <span>等级配置</span>
                </div>
                {errorMessage ? (
                    <Alert className="member-level-error" message={errorMessage} role="alert" type="error" />
                ) : null}
                <div className="member-level-card-list">
                    {levels.map(record => {
                        const variant = getMemberCardVariant(record.id);
                        return (
                            <div key={record.id} className={`member-level-card member-level-card--${variant}`}>
                                <span className={`member-level-card__name member-level-card__name--${variant}`}>
                                    {record.name || DEFAULT_VALUE}
                                </span>
                                {record.id > 1 ? (
                                    <div className={`member-level-card__fee member-level-card__fee--${variant}`}>
                                        <p>费用</p>
                                        <span> {formatFee(record)}</span>
                                    </div>
                                ) : null}
                                <div className="member-level-card__actions">
                                    <button
                                        type="button"
                                        className="member-level-card__action"
                                        aria-label={`修改${record.name || "会员"}`}
                                        disabled={banButton}
                                        onClick={() => navigate(`/lianYunUser/memberLevelinfo?id=${record.id}`)}
                                    >
                                        <i aria-hidden="true" className="iconfont icon-edit" />
                                        修改
                                    </button>
                                    {record.id > 1 ? (
                                        <button
                                            type="button"
                                            className="member-level-card__action"
                                            aria-label={`${isOpen(record) ? "关闭" : "开放"}${record.name || "会员"}`}
                                            disabled={banButton}
                                            onClick={() => handleState(record)}
                                        >
                                            <i
                                                aria-hidden="true"
                                                className={`iconfont ${isOpen(record) ? "icon-close" : "icon-a-Frame27"}`}
                                            />
                                            {isOpen(record) ? "关闭" : "开放"}
                                        </button>
                                    ) : null}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            <div className="member-level-section member-level-register">
                <div className="member-level-section__title">
                    <span className="member-level-section__title-tag" aria-hidden="true" />
                    <span>注册送会员配置</span>
                </div>
                {registerEditing ? (
                    <div className="member-level-register-form">
                        <div className="member-level-register-form__row member-level-register-form__row--full">
                            <label className="member-level-register-form__label" htmlFor="register-give-open">
                                <RequiredMark />
                                <span>是否开启</span>
                            </label>
                            <Radio.Group
                                id="register-give-open"
                                aria-label="是否开启注册送会员"
                                options={[
                                    { label: "是", value: 1 },
                                    { label: "否", value: 0 }
                                ]}
                                value={registerConfig.hasOpen ?? 0}
                                onChange={event => updateRegisterField("hasOpen", event.target.value)}
                            />
                        </div>
                        <div className="member-level-register-form__row">
                            <label className="member-level-register-form__field">
                                <span>
                                    <RequiredMark />
                                    赠送会员类型
                                </span>
                                <Radio.Group
                                    aria-label="赠送会员类型"
                                    options={openRegisterLevels.map(record => ({
                                        label: record.name || DEFAULT_VALUE,
                                        value: record.id
                                    }))}
                                    value={registerConfig.level}
                                    onChange={event => updateRegisterField("level", event.target.value)}
                                />
                            </label>
                            <label className="member-level-register-form__field">
                                <span>
                                    <RequiredMark />
                                    赠送时长
                                </span>
                                <Input
                                    aria-label="注册送会员时长"
                                    type="number"
                                    suffix="天"
                                    value={registerConfig.duration ?? ""}
                                    onChange={event => updateRegisterField("duration", numberValue(event.target.value))}
                                />
                            </label>
                            <label className="member-level-register-form__field">
                                <span>
                                    <RequiredMark />
                                    兑换有效期
                                </span>
                                <Input
                                    aria-label="注册送会员兑换有效期"
                                    type="number"
                                    suffix="天"
                                    value={registerConfig.effectiveDuration ?? ""}
                                    onChange={event =>
                                        updateRegisterField("effectiveDuration", numberValue(event.target.value))
                                    }
                                />
                            </label>
                        </div>
                    </div>
                ) : (
                    <DetailPanel businessConfig={registerConfig} />
                )}
                <div className="member-level-register__actions">
                    {registerEditing ? (
                        <>
                            <Button aria-label="保存注册送会员配置" type="primary" onClick={() => void saveRegister()}>
                                保存
                            </Button>
                            <Button
                                type="primary"
                                ghost
                                onClick={() => {
                                    setRegisterConfig(savedRegisterConfig);
                                    setRegisterEditing(false);
                                }}
                            >
                                取消
                            </Button>
                        </>
                    ) : (
                        <Button aria-label="修改注册送会员配置" type="primary" onClick={() => setRegisterEditing(true)}>
                            修改
                        </Button>
                    )}
                </div>
            </div>
            <Modal
                open={Boolean(confirmState)}
                title={confirmState?.nextState ? "是否开放" : "是否关闭"}
                onCancel={() => setConfirmState(undefined)}
                onOk={() => void confirmStateChange()}
                okText="确认"
                cancelText="取消"
                okButtonProps={{ "aria-label": "确认" }}
                cancelButtonProps={{ "aria-label": "取消" }}
            >
                {confirmState?.nextState
                    ? `开放后，用户将可以购买或兑换${confirmState.name}`
                    : `关闭后，用户将不能购买或兑换${confirmState?.name}`}
            </Modal>
        </section>
    );
}

export function MemberLevelFormPage({
    memberId,
    loadDetail = loadMemberLevelDetail,
    updateMember = updateMemberLevel
}: MemberLevelFormPageProps) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const banButton = useUserStore(state => state.banButton);
    const resolvedMemberId = memberId ?? (Number(searchParams.get("id") || 0) || undefined);
    const [form, setForm] = useState<MemberLevelRecord>({
        id: resolvedMemberId || 0,
        name: "",
        yearDiscountFlag: 0,
        giftFlag: 0
    });
    const [permissionOptions, setPermissionOptions] = useState<MemberPermissionOption[]>(createPermissionOptions);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        if (resolvedMemberId == null) return;
        void loadDetail(resolvedMemberId)
            .then(detail => {
                setForm(detail);
                setPermissionOptions(hydratePermissionOptions(detail.memberPermission || detail.feature));
            })
            .catch(() => setErrorMessage("会员等级详情加载失败"));
    }, [loadDetail, resolvedMemberId]);

    const updateField = <K extends keyof MemberLevelRecord>(key: K, value: MemberLevelRecord[K]) =>
        setForm(current => ({ ...current, [key]: value }));

    /** 更新单条权限行，保持 React 状态不可变并兼容 Vue props 结构。 */
    const updatePermission = (index: number, patch: Record<string, unknown>) => {
        setPermissionOptions(current =>
            current.map((option, optionIndex) =>
                optionIndex === index ? { ...option, props: { ...option.props, ...patch } } : option
            )
        );
    };

    /** 数值权限沿用 Vue 的大于 0 且小于 10000 校验。 */
    const validatePermissions = () => {
        const invalid = permissionOptions.some(option => {
            const props = option.props;
            const fields =
                option.layout === 2 && !props.hasUnLimit
                    ? ["times"]
                    : option.layout === 5 && props.hasSupport
                      ? ["supportNum"]
                      : option.layout === 6 && props.hasSupport
                        ? ["boothNum", "joinNum", "quotaAmount"]
                        : option.layout === 7 && props.hasSupport
                          ? ["freeNum"]
                          : [];
            return fields.some(field => {
                const value = Number(props[field]);
                return !Number.isFinite(value) || value < 1 || value >= 10000;
            });
        });
        if (invalid) setErrorMessage("权限数值需大于0且小于10000");
        return !invalid;
    };

    /** 年费、折扣和赠送时长校验，保留旧 Vue 表单边界。 */
    const validateFee = () => {
        if (form.id === 1) return true;
        const yearFee = Number(form.yearFee);
        if (!Number.isFinite(yearFee) || yearFee < 0.01 || yearFee > 999999.99) {
            setErrorMessage("年度费用需大于0且小于1000000");
            return false;
        }
        if (Number(form.yearDiscountFlag) === ANNUAL_MEMBER_STATE.DISCOUNT.code) {
            const discountFee = Number(form.yearDiscountFee);
            if (!Number.isFinite(discountFee) || discountFee < 0.01 || discountFee >= yearFee) {
                setErrorMessage("折扣价必须小于原价");
                return false;
            }
        }
        if (Number(form.giftFlag) === GIFT_MEMBER_STATE.GIFT.code) {
            const duration = Number(form.giftDuration);
            if (!Number.isFinite(duration) || duration < 1 || duration >= 1000000) {
                setErrorMessage("赠送时长需大于0且小于1000000");
                return false;
            }
        }
        return true;
    };

    const submit = async () => {
        setErrorMessage("");
        if (!validateFee() || !validatePermissions()) return;
        try {
            const memberPermission = buildMemberPermission(permissionOptions);
            await updateMember({
                ...form,
                yearDiscountFee:
                    Number(form.yearDiscountFlag) === ANNUAL_MEMBER_STATE.DISCOUNT.code
                        ? form.yearDiscountFee
                        : undefined,
                memberPermission
            });
            navigate(-1);
        } catch {
            setErrorMessage("会员等级保存失败");
        }
    };

    const renderPermissionControl = (option: MemberPermissionOption, index: number) => {
        const props = option.props;
        const setNumber = (field: string, value: string) => updatePermission(index, { [field]: numberValue(value) });
        if (option.layout === 1) {
            const visibleRange = Array.isArray(props.visibleRange) ? (props.visibleRange as number[]) : [1];
            return (
                <>
                    <label>
                        <input
                            type="radio"
                            checked={props.hasUnLimit === 1}
                            onChange={() => updatePermission(index, { hasUnLimit: 1 })}
                        />{" "}
                        无限制
                    </label>
                    <label>
                        <input
                            type="radio"
                            checked={props.hasUnLimit === 0}
                            onChange={() => updatePermission(index, { hasUnLimit: 0 })}
                        />{" "}
                        限制
                    </label>
                    {!props.hasUnLimit ? (
                        <span className="member-level-form-page__visible-content ml-4">
                            <span className="member-level-form-page__content-label">请配置可见内容</span>
                            {VISIBLE_CONTENT_STATE.options.map(item => (
                                <label key={String(item.value)} className="mr-3">
                                    <input
                                        type="checkbox"
                                        checked={visibleRange.includes(Number(item.value))}
                                        disabled={Number(item.value) === 1}
                                        onChange={event => {
                                            const next = event.target.checked
                                                ? [...visibleRange, Number(item.value)]
                                                : visibleRange.filter(value => value !== Number(item.value));
                                            updatePermission(index, { visibleRange: [...new Set(next)].sort() });
                                        }}
                                    />
                                    {item.label}
                                </label>
                            ))}
                        </span>
                    ) : null}
                </>
            );
        }
        if (option.layout === 2) {
            return (
                <div className="member-level-form-page__limit-control">
                    <label>
                        <input
                            type="radio"
                            checked={props.hasUnLimit === 1}
                            onChange={() => updatePermission(index, { hasUnLimit: 1 })}
                        />{" "}
                        无限制
                    </label>
                    <label>
                        <input
                            type="radio"
                            checked={props.hasUnLimit === 0}
                            onChange={() => updatePermission(index, { hasUnLimit: 0 })}
                        />{" "}
                        限制
                    </label>
                    {!props.hasUnLimit ? (
                        <>
                            <span className="member-level-form-page__content-label">限制内容</span>
                            <Input
                                aria-label={`${option.label}-限制次数`}
                                suffix="次/天"
                                type="number"
                                placeholder="请输入"
                                value={(props.times as number | undefined) ?? ""}
                                onChange={event => setNumber("times", event.target.value)}
                            />
                        </>
                    ) : null}
                </div>
            );
        }
        if (option.layout === 3) {
            return (
                <>
                    <label>
                        <input
                            type="radio"
                            checked={props.hasUnLimit === 1}
                            onChange={() => updatePermission(index, { hasUnLimit: 1 })}
                        />{" "}
                        无限制
                    </label>
                    <label>
                        <input
                            type="radio"
                            checked={props.hasUnLimit === 0}
                            onChange={() => updatePermission(index, { hasUnLimit: 0 })}
                        />{" "}
                        限制
                    </label>
                </>
            );
        }
        if (option.layout === 4) {
            return (
                <>
                    <label>
                        <input
                            type="radio"
                            checked={props.hasSupport === 0}
                            onChange={() => updatePermission(index, { hasSupport: 0 })}
                        />{" "}
                        不支持
                    </label>
                    <label>
                        <input
                            type="radio"
                            checked={props.hasSupport === 1}
                            onChange={() => updatePermission(index, { hasSupport: 1 })}
                        />{" "}
                        支持
                    </label>
                </>
            );
        }
        const supportFields =
            option.layout === 5
                ? [["supportNum", "支持数量", "个"]]
                : option.layout === 6
                  ? [
                        ["boothNum", "免费提供展位数量", "个/场"],
                        ["joinNum", "参与人数", "人/场"],
                        ["quotaAmount", "名额价值", "元"]
                    ]
                  : [["freeNum", "免费推广次数", "条"]];
        return (
            <>
                <label>
                    <input
                        type="radio"
                        checked={props.hasSupport === 0}
                        onChange={() => updatePermission(index, { hasSupport: 0 })}
                    />{" "}
                    不支持
                </label>
                <label>
                    <input
                        type="radio"
                        checked={props.hasSupport === 1}
                        onChange={() => updatePermission(index, { hasSupport: 1 })}
                    />{" "}
                    支持
                </label>
                {props.hasSupport
                    ? supportFields.map(([field, label, suffix]) => (
                          <label key={field} className="ml-4">
                              {label}
                              <Input
                                  aria-label={`${option.label}-${label}`}
                                  type="number"
                                  value={(props[field] as number | undefined) ?? ""}
                                  onChange={event => setNumber(field, event.target.value)}
                              />{" "}
                              {suffix}
                          </label>
                      ))
                    : null}
                {option.layout === 7 && props.hasSupport ? (
                    <Select
                        aria-label="免费推广次数单位"
                        options={FREE_NUM_UNIT.options}
                        value={props.freeNumUnit || 2}
                        onChange={value => updatePermission(index, { freeNumUnit: Number(value) })}
                    />
                ) : null}
            </>
        );
    };

    return (
        <section aria-label="member-level-form-page" className="member-level-page member-level-form-page">
            {/* Vue 版表单使用 section-title 作为唯一页内标题，避免布局标题与页面标题重复。 */}
            <div className="member-level-section__title member-level-form-page__title">
                <span className="member-level-section__title-tag" aria-hidden="true" />
                权限设置
            </div>
            {errorMessage ? (
                <Alert className="member-level-error" message={errorMessage} role="alert" type="error" />
            ) : null}
            {form.id !== 1 ? (
                <div className="member-level-form-page__top-grid">
                    <div className="member-level-form-page__line">
                        <span className="member-level-form-page__label">年度费用</span>
                        <label>
                            <input
                                type="radio"
                                checked={Number(form.yearDiscountFlag) === 0}
                                onChange={() => updateField("yearDiscountFlag", 0)}
                            />
                            不优惠
                        </label>
                        <label>
                            <input
                                type="radio"
                                checked={Number(form.yearDiscountFlag) === 1}
                                onChange={() => updateField("yearDiscountFlag", 1)}
                            />
                            优惠
                        </label>
                        <span className="member-level-form-page__label member-level-form-page__label--required">
                            {Number(form.yearDiscountFlag) === 1 ? "折扣价" : "价格"}
                        </span>
                        <Input
                            aria-label="年度费用"
                            className="member-level-form-page__input"
                            suffix="元"
                            type="number"
                            value={form.yearDiscountFlag === 1 ? (form.yearDiscountFee ?? "") : (form.yearFee ?? "")}
                            onChange={event =>
                                updateField(
                                    form.yearDiscountFlag === 1 ? "yearDiscountFee" : "yearFee",
                                    numberValue(event.target.value)
                                )
                            }
                        />
                    </div>
                    <div className="member-level-form-page__line">
                        <span className="member-level-form-page__label">赠送会员</span>
                        <label>
                            <input
                                type="radio"
                                checked={Number(form.giftFlag) === 0}
                                onChange={() => updateField("giftFlag", 0)}
                            />
                            不赠送
                        </label>
                        <label>
                            <input
                                type="radio"
                                checked={Number(form.giftFlag) === 1}
                                onChange={() => updateField("giftFlag", 1)}
                            />
                            赠送
                        </label>
                        {Number(form.giftFlag) === 1 ? (
                            <>
                                <span className="member-level-form-page__label">赠送时长</span>
                                <Input
                                    aria-label="赠送时长"
                                    className="member-level-form-page__input"
                                    type="number"
                                    value={form.giftDuration ?? ""}
                                    onChange={event => updateField("giftDuration", numberValue(event.target.value))}
                                />
                                <Select
                                    aria-label="赠送时长单位"
                                    className="member-level-form-page__unit"
                                    options={GIFT_DURATION_UNIT.options}
                                    value={form.giftDurationUnit || GIFT_DURATION_UNIT.DAY.code}
                                    onChange={value => updateField("giftDurationUnit", Number(value))}
                                />
                            </>
                        ) : null}
                    </div>
                </div>
            ) : null}
            <div className="member-level-section__title member-level-form-page__config-title">
                <span className="member-level-section__title-tag" aria-hidden="true" />
                配置权限
            </div>
            <div className="config-container">
                {permissionOptions.map((option, index) => (
                    <div className="row-wrap" key={option.key}>
                        <div className="label-wrap">{option.label}</div>
                        <div className="field-wrap">{renderPermissionControl(option, index)}</div>
                    </div>
                ))}
            </div>
            <div className="submit operation-fixed-btn">
                <Button type="primary" aria-label="保存会员配置" disabled={banButton} onClick={() => void submit()}>
                    保存
                </Button>
                <Button onClick={() => navigate(-1)}>返回</Button>
            </div>
        </section>
    );
}
